import React, { useEffect, useState } from 'react';
import { Button, Snackbar } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import DialogInviteUser from '../../controls/dialogs/DialogInviteUser';

import { userManagementService } from '../../../services/user-management.service';
import { authService } from '../../../services/auth.service';
import { inviteRoleOptions, OrganisatieUserRole } from '../../../models/user/userRole';

interface InviteUserProps {
  organisatieId: string; // ID of the current organisatie
  callback: () => void; // Callback function to trigger refresh
}

const InviteUser: React.FC<InviteUserProps> = ({ organisatieId, callback }) => {
  const [isAdmin, setAdmin] = useState<boolean>(false);
  const [canCrud, setCanCrud] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    snackbar: false,
    inviteUser: false
  });

  useEffect(() => {
    setIsAdmin();
  }, []);

  const setIsAdmin = async () => {
    const isAdmin = await authService.isAdmin();
    setAdmin(isAdmin);
    setCanCrud(authService.canCrud());
  }

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const handleSubmitInviteUser = async (organisatieId: string, formValues: Record<string, string>) => {
    setLoading(true);

    const email = formValues.email;
    
    if (!email) {
      // console.log('E-mailadres ontbreekt in formValues', formValues);
      setError('E-mailadres ontbreekt');
      setLoading(false);
      return;
    }

    try {
      const role = formValues.role as OrganisatieUserRole;

      if (!role) {
        setError('Rol ontbreekt');
        setLoading(false);
        return;
      }

      const response = await userManagementService.inviteUserForOrganisatie(organisatieId, email, role);

      if (response.ok) {
        // console.log('User invited successfully:', response);
        closeDialog('inviteUser');
        
        setTimeout(() => {
          if (callback) callback();
          setLoading(false);

          
          setSnackbarMessage('Uitnodiging is verstuurd! Het kan soms even duren voordat het overzicht is bijgewerkt.');
          openDialog('snackbar');
        }, 2000);
      }
    }
    catch (error: any) {
      const status = error?.status || error?.response?.status;
      const message = error?.message || "Er is een fout opgetreden";

      if (status === 500) {
        // 👇 Show your custom message for server errors
        setError('Er is een fout opgetreden. Probeer het later opnieuw.');
      }
      else {
        // Handle other errors if needed
        console.error(`Er is een fout opgetreden (${status}):`, message);
        setError(message);
      }
      setLoading(false);
    }
  };
  
  return (
    <React.Fragment>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key="topcenter"
      />

      {canCrud && (
        <Button 
          variant="text" 
          color="secondary"
          startIcon={<SendIcon />}  
          onClick={() => {
            openDialog('inviteUser');
          }}>
          Gebruiker uitnodigen
        </Button>
      )}

      <DialogInviteUser
        open={open.inviteUser}
        organisatieId={organisatieId}
        allowedRoles={isAdmin ? inviteRoleOptions : inviteRoleOptions.filter((role) => role !== 'keyUser')}
        onClose={() => closeDialog('inviteUser')}
        onSubmit={handleSubmitInviteUser}
      />

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </React.Fragment>
  );
};

export default InviteUser;
